<template>
  <div>
    <TitleH1 title="Фотогалерея" />
    <Filters
      @fetchPhotoGallery="initPage"
      @resetFilters="resetFilters"
      @updateSystem="updateSystem"
      @updateModel="updateModel"
      @updateMaterial="updateMaterial"
      :systemsAndModels="systemsAndModels"
      :materials="materials"
      :filters="filters"
    />
    <div
      :class="[
        'wrapper',
        {
          'wrapper-indent': isPagination,
        },
      ]"
    >
      <div class="container">
        <div class="list">
          <CardItem
            class="list-item"
            v-for="item in items"
            @clickSlider="clickSlider(item)"
            :key="item.id"
            :item="item"
          />
        </div>
      </div>
    </div>

    <Pagination
      v-if="isPagination"
      @updatePagination="updatePagination"
      :pagination="pagination"
      :per_page="false"
    />

    <transition name="fade">
      <SliderImages
        v-if="windowSlider && windowSlider.length"
        @closeSlider="closeSlider"
        :items="windowSlider"
        imageKey="photo"
      >
        <div v-if="itemSlider" class="slider-comment">
          <BaseTypography
            v-if="itemSlider.system || itemSlider.model"
            size="14xl"
            colorSchema="white"
            fontWeight="600"
            attr="span"
          >
            {{ itemSlider.system }} / {{ itemSlider.model }}
            <br />
            {{ itemSlider.material }}
          </BaseTypography>

          <BaseTypography
            v-if="itemSlider.comment"
            class="slider-comment-text"
            size="14xl"
            colorSchema="white"
            >{{ itemSlider.comment }}</BaseTypography
          >
        </div>
      </SliderImages>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'PhotoGallery',
  components: {
    TitleH1: () => import('@/elements/Meta/TitleH1'),
    Filters: () => import('./components/filters/Filters'),
    CardItem: () => import('./components/CardItem'),
    SliderImages: () => import('@/components/slider-images'),
    Pagination: () => import('@/pages/account/components/Pagination'),
    BaseTypography: () => import('@/components/base/BaseTypography'),
  },
  metaInfo() {
    return {
      title: `Фотогалерея - Личный кабинет дилера - ${this.getDealerName}`,
    };
  },

  data: () => ({
    filters: {
      system: null,
      model: null,
      material: null,
    },

    pagination: {
      last_page: 1,
      page: 1,
    },

    systemsAndModels: [],
    materials: [],

    items: [],
    itemSlider: null,
  }),

  computed: {
    ...mapGetters('user', ['getDealerName']),

    windowSlider() {
      return this.itemSlider?.photos;
    },

    isPagination() {
      return this.pagination.last_page > 1;
    },
  },

  methods: {
    setUrlQueryFilters() {
      const filters = this.filters;

      const query = {
        ...(filters.system?.id && {
          system: filters.system.id,
        }),
        ...(filters.model?.id && {
          model: filters.model.id,
        }),
        ...(filters.material?.id && {
          material: filters.material.id,
        }),
        ...(filters.is_my && {
          is_my: filters.is_my,
        }),
        ...(this.pagination?.page > 1 && {
          page: this.pagination?.page,
        }),
      };

      this.$router.push({ 
        name: this.$route.name,
        query
      });
    },

    async updateFilters(newFilters) {
      if (newFilters) {
        this.filters = {
          ...this.filters,
          ...newFilters,
        };
      }

      this.setUrlQueryFilters();

      if (newFilters && !newFilters.material) {
        await this.fetchMaterials({
          system: this.filters.system?.id,
          model: this.filters.model?.id,
        });
      }
      await this.fetchPhotoGallery();
    },

    resetFilters() {
      const filters = this.filters;

      for (const key in filters) {
        this.filters[key] = typeof this.filters[key] === 'boolean' ? false : null;
      }
      this.setUrlQueryFilters();
      this.$nextTick(() => {
        this.initPage();
      });
    },

    clickSlider(item) {
      this.itemSlider = item;
    },

    closeSlider() {
      this.itemSlider = null;
    },

    async updateSystem(system) {
      if (this.filters.system && this.filters.system.id !== system.id) {
        this.updateFilters({ system, model: null, material: null });
      } else if (this.filters.system?.id === system.id) {
        this.updateFilters({ system: null, model: null, material: null });
      } else {
        this.updateFilters({ system, material: null });
      }
    },

    async updateModel(model) {
      if (this.filters?.model?.id === model?.id) {
        this.updateFilters({ model: null });
      } else if (this.filters.system && model) {
        const modelFind = this.getItemById([this.filters.system], model.id);

        if (!modelFind) {
          const systemFind = this.getSystemItemByModelId(model.id);

          this.updateFilters({ system: systemFind ?? null, model });
        } else {
          this.updateFilters({ model });
        }
      } else if (model) {
        const systemFind = this.getSystemItemByModelId(model?.id);
        this.updateFilters({ system: systemFind ?? null, model: model ?? null });
      } else {
        this.updateFilters({ model });
      }
    },

    updateMaterial(material) {
      this.updateFilters({ material });
    },

    updatePagination(page) {
      this.pagination.page = page;
      this.updateFilters();
    },

    async fetchMaterials({ system = null, model = null }) {
      try {
        const materialsRes = await this.$api.methods.photoGallery.filter.materials.get({
          system,
          model,
        });

        this.setDataMaterials(materialsRes.data);
      } catch {
        this.$danger('Ошибка в запросе материалов');
      }
    },

    async fetchPhotoGallery() {
      let filters = null;
      for (const key in this.filters) {
        if (typeof this.filters[key] === 'boolean' && this.filters[key]) {
          filters = {
            ...(filters || {}),
            [key]: [this.filters[key]],
          };
        } else if (this.filters[key]) {
          filters = {
            ...(filters || {}),
            [key]: [this.filters[key].id],
          };
        }
      }

      const params = {
        ...(filters && { filters }),
        ...(this.pagination.page > 1 && {
          page: this.pagination.page,
        }),
      };

      try {
        this.togglePreload();
        const { data } = await this.$api.methods.photoGallery.get(params);

        this.items = data.data;

        this.pagination.last_page = data.pagination.last_page;
        this.pagination.page = data.pagination.current_page;
      } catch (err) {
        console.error(err);
        this.$danger('Ошибка в запросе фотогалереи');
      } finally {
        this.togglePreload();
      }
    },

    getSystemItemByModelId(modelId, items) {
      const list = items ? items : this.systemsAndModels;

      return list.find(item => {
        if (!items && item.items?.length) {
          return this.getSystemItemByModelId(modelId, item.items);
        }

        if (String(item.id) === String(modelId)) {
          return true;
        }

        if (!item.items?.length) {
          return false;
        }

        return this.getSystemItemByModelId(modelId, item.items);
      });
    },

    getItemById(items = [], byId) {
      return items.reduce((acc, value) => {
        if (acc) {
          return acc;
        }

        if (String(value.id) === String(byId)) {
          return value;
        }

        if (!value?.items?.length) {
          return acc;
        }

        return this.getItemById(value.items, byId);
      }, null);
    },

    setDataMaterials(data = []) {
      this.materials = data.map(({ materials = [], ...rest }) => ({
        ...rest,
        items: materials?.map(item => ({
          ...item,
          items: [],
        })),
      }));
    },

    getCurretModelById(modelId, items = null) {
      const list = items ? items : this.systemsAndModels;

      return list.reduce((acc, value) => {
        if (acc) {
          return acc;
        }
        if (!items && value.items) {
          return this.getCurretModelById(modelId, value.items);
        }
        if (String(value.id) === String(modelId)) {
          return value;
        }

        if (!value.items?.length) {
          return null;
        }

        return this.getCurretModelById(modelId, value.items);
      }, null);
    },

    async initPage() {
      const urlQuery = this.$route.query;

      if (urlQuery?.is_my) {
        this.filters.is_my = urlQuery.is_my === 'true';
      }

      if (urlQuery?.page) {
        this.pagination.page = urlQuery.page;
      }

      try {
        const systemsAndModelsRes = await this.$api.methods.photoGallery.filter.systemsAndModels.get();

        if (systemsAndModelsRes?.data?.length) {
          this.systemsAndModels = systemsAndModelsRes.data.map(({ models = [], ...rest }) => ({
            ...rest,
            items: models.map(({ sub_models = [], ...modelRest }) => ({
              ...modelRest,
              items: sub_models,
            })),
          }));
        }

        const systemFind = urlQuery.system
          ? this.systemsAndModels.find(({ id }) => String(id) === String(urlQuery.system))
          : null;

        if (systemFind) {
          this.filters.system = systemFind;
        }

        if (urlQuery.model) {
          const modelFind = this.getCurretModelById(urlQuery.model);

          if (modelFind) {
            this.filters.model = modelFind;
          }
        }

        const materialsRes = await this.$api.methods.photoGallery.filter.materials.get({
          ...(this.filters.system?.id && { system: this.filters.system.id }),
        });

        this.setDataMaterials(materialsRes.data);

        if (urlQuery?.material) {
          const materialFind = this.getItemById(this.materials, urlQuery.material);

          if (materialFind) {
            this.filters.material = materialFind;
          }
        }

        this.$nextTick(() => {
          this.fetchPhotoGallery();
        });
      } catch (err) {
        console.error(err);
        this.$danger('Ошибка в запросе фотогалереи');
      }
    },
  },

  created() {
    this.initPage();
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  justify-content: center;
  padding: 0 20px;

  &-indent {
    padding-bottom: 68px;
  }

  @include maxMobile {
    padding: 0;
  }
}

.slider-comment {
  padding: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &-text {
    margin-top: 4px;
    white-space: pre-wrap;
  }
}

.container {
  max-width: calc((400px + 12px * 2) * 4);
  width: 100%;

  @include desktop1740 {
    max-width: calc((400px + 12px * 2) * 3);
  }

  @include maxDesktop1300 {
    max-width: calc((400px + 8px * 2) * 2);
  }

  @include maxMobile860 {
    max-width: 100%;
  }
}

.list {
  display: flex;
  flex-wrap: wrap;

  &-item {
    margin: 12px;
  }

  @include maxDesktop1440 {
    &-item {
      margin: 8px;
    }
  }

  @include maxMobile {
    &-item {
      margin: 3px;
    }
  }

  @include maxMobile860 {
    justify-content: center;
  }
}
</style>
